import { el, toast } from '../../dom';
import { villageApi, type VillageState, type Villager, type VillageWorkplace, type TaskType, type TaskRun } from '../../game-api';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { BUILDINGS, closePlanter, openOverlay, type FarmCtx, refreshBag,} from './context';
import { modalWrap } from '../../ui/panel';
import { openConfirm } from './confirm-dialog';
import { richText, gicon, type IconName } from '../../ui/icons';
import { TILE } from '../../map/world';
import { createVillagers, villagerName } from '../../map/villagers';
import { findWorkNode } from './scene';
import { openHusbandry } from './husbandry';

// mushroom → the mushroom, not the currency token: a forager produces foraged
// mushrooms, and showing the Shroom icon there said he was minting money.
const PRODUCE_ICON: Record<string, IconName> = { wood: 'wood', stone: 'stone', mushroom: 'mushroom' };
export const KIND_SPRITE: Record<string, string> = {
  forester: '/sprites/npc/forester.png',
  miner: '/sprites/npc/miner.png',
  forager: '/sprites/npc/forager.png',
  farmer: '/sprites/npc/farmer.png',
  herder: '/sprites/npc/herder.png',
};

// ─── Who lives / works here ──────────────────────────────────────────────────

/**
 * Beds per building kind. Mirrors the server's HOME_SLOTS (village.config.ts) —
 * the same table decides the population cap, so a client that disagreed would
 * seat a villager in a house the server does not think exists.
 */
export const HOME_SLOTS: Record<string, number> = { house: 1, farmhouse: 2 };

/**
 * Buildings that can have somebody in them — homes plus every workplace kind
 * (mirrors the server's WORKPLACES). Anything else (a torch, a fence) never asks
 * the server who lives there, so clicking scenery costs no request.
 */
export const OCCUPIABLE_KINDS: ReadonlySet<string> = new Set([
  ...Object.keys(HOME_SLOTS), 'lumber_hut', 'mine_shaft', 'forage_hut', 'barn',
]);

export interface Occupant {
  id: string;
  name: string;
  kind: string;
  /** Works this building (assigned workplace) vs sleeps here (housing slot). */
  role: 'worker' | 'resident';
  /** One line of what they do, for the row under the name. */
  detail: string;
}

/**
 * Which villagers belong to the building at (b.tx, b.ty).
 *
 * WORKERS are recorded: the villager row stores jobTx/jobTy, so "who works this
 * hut" is a fact, not a guess.
 *
 * RESIDENCY is not stored anywhere — a summoned villager gets a home TILE near
 * the player, unrelated to any house — so it is DERIVED here, and derived the
 * same way every time: homes sorted by position, villagers sorted by their
 * persisted id, beds filled in order. Both keys are persisted, so the answer
 * survives a reload; it only shifts when a home is actually built or demolished,
 * which is a real change in the village rather than a reshuffle.
 */
export function occupantsOf(
  b: { kind: string; tx: number; ty: number },
  villagers: readonly Villager[],
  buildings: readonly { kind: string; tx: number; ty: number; isReady?: boolean }[],
): Occupant[] {
  const named = (v: Villager, role: Occupant['role'], detail: string): Occupant =>
    ({ id: v.id, name: villagerName(v.id, v.name), kind: v.kind, role, detail });

  const out: Occupant[] = [];
  const seen = new Set<string>();
  for (const v of villagers) {
    if (v.job === b.kind && v.jobTx === b.tx && v.jobTy === b.ty) {
      const what = v.produces ? `${v.kind} · ${v.ratePerHour}/h here` : `${v.kind} · ${v.task ? v.task.name : 'no standing order'}`;
      out.push(named(v, 'worker', what));
      seen.add(v.id);
    }
  }

  const beds = HOME_SLOTS[b.kind] ?? 0;
  if (beds > 0) {
    // Unfinished homes have no beds yet — the server does not count them toward
    // the cap either, so nobody may be shown living in a construction site.
    const homes = buildings
      .filter((h) => (HOME_SLOTS[h.kind] ?? 0) > 0 && h.isReady !== false)
      .sort((p, q) => p.ty - q.ty || p.tx - q.tx || p.kind.localeCompare(q.kind));
    const roster = [...villagers].sort((p, q) => p.id.localeCompare(q.id));
    let i = 0;
    for (const h of homes) {
      const slots = HOME_SLOTS[h.kind] ?? 0;
      const mine = h.tx === b.tx && h.ty === b.ty;
      for (let s = 0; s < slots; s++, i++) {
        const v = roster[i];
        if (!v || !mine || seen.has(v.id)) continue;
        out.push(named(v, 'resident', v.jobName ? `${v.kind} · works the ${v.jobName}` : `${v.kind} · no job yet`));
        seen.add(v.id);
      }
      if (mine) break;   // past our building — the rest of the roster lives elsewhere
    }
  }
  return out;
}

/**
 * The orders this villager could hold, and whether it is standing where it needs
 * to be to hold one.
 *
 * A task is gated on TWO things server-side (task.service): the villager's kind,
 * and the workplace it is currently assigned to. Mirroring the second one here is
 * what turns "Only a Farmhand can…" / "…must be assigned to a farmhouse first"
 * from a 400 you discover after committing into a disabled picker that says why.
 */
export function tasksFor(v: Villager, types: TaskType[]): { def: TaskType; ready: boolean }[] {
  return types
    .filter((t) => t.villagerKind === v.kind)
    .map((t) => ({ def: t, ready: v.job === t.requiresJob }));
}

/** "18/48 ready · 6/h" — a charge is one bed watered or one animal fed. */
function chargeLabel(task: { charges: number; maxCharges: number }, def?: TaskType): string {
  return `${task.charges}/${task.maxCharges} ready${def ? ` · ${def.chargesPerHour}/h` : ''}`;
}

/** One-line summary of what a completed run actually did, for the toast. */
export function describeRun(r: TaskRun): string {
  if (r.used === 0) return 'Nothing to do just now';
  if (r.kind === 'tend_plots') return `Watered ${r.watered.length} bed${r.watered.length === 1 ? '' : 's'}`;
  const collected = Object.values(r.collected).reduce((n, q) => n + q, 0);
  const fed = `Fed ${r.fed.length} animal${r.fed.length === 1 ? '' : 's'}`;
  return collected > 0 ? `${fed} · banked ${collected} produce` : fed;
}

/** A home tile near the player for a newly-summoned villager. */
function homeTileNearPlayer(c: FarmCtx): { tx: number; ty: number } {
  const px = c.player ? Math.floor(c.player.x / TILE) : 0;
  const py = c.player ? Math.floor(c.player.y / TILE) : 0;
  return { tx: px + (Math.floor(Math.random() * 5) - 2), ty: py + (Math.floor(Math.random() * 5) - 2) };
}

export function openVillage(c: FarmCtx) {
  void dispatchAction('ui.village', async () => {
    const state = await villageApi.get();
    renderVillage(c, state);
  }).catch((e) => { if (!isDropped(e)) toast('Could not open the village', 'error'); });
}

/**
 * Rebuild the on-map villagers so a summon / job change shows up immediately
 * (the scene only builds them once, on load). Best-effort ambience: if the
 * entity layer is gone or a sprite fails to load, the panel still works.
 */
async function refreshMapVillagers(c: FarmCtx, st: VillageState) {
  if (!c.alive || !c.entityLayer) return;
  try {
    c.villagers?.destroy();
    c.villagers = null;
    if (!st.villagers.length) return;
    c.villagers = await createVillagers(st.villagers, c.entityLayer,
      (kinds, wx, wy, r) => findWorkNode(c, kinds, wx, wy, r));
  } catch { /* ambience only */ }
}

/** Workplaces this villager may work: free ones, plus the one it already has. */
function optionsFor(v: Villager, st: VillageState): VillageWorkplace[] {
  return st.workplaces.filter((w) =>
    w.accepts.includes(v.kind) && (w.workerId === null || w.workerId === v.id));
}

/**
 * The standing-order strip under a villager that can hold one.
 *
 * Charges are the whole economy of a task: one charge = one bed watered or one
 * animal fed, banked at `chargesPerHour` up to a cap, and a run spends only what
 * it uses. So the strip states the bank, what a charge buys, and nothing else.
 */
function taskBlock(c: FarmCtx, v: Villager, orders: { def: TaskType; ready: boolean }[]): HTMLElement {
  const box = el('div', 'mt-2 pt-2 border-t border-border/60');
  const pick = el('select', 'bg-surface border border-border rounded text-[11px] px-1 py-0.5 w-full', {
    onChange: (e: Event) => void doSetTask(c, v.id, (e.target as HTMLSelectElement).value || null),
  }) as HTMLSelectElement;
  pick.append(el('option', '', { value: '' }, 'No standing order'));
  for (const { def, ready } of orders) {
    const o = el('option', '', { value: def.kind }, ready ? def.name : `${def.name} (needs a ${BUILDINGS[def.requiresJob]?.name ?? def.requiresJob})`) as HTMLOptionElement;
    // The server refuses the order outright when the villager is not at the right
    // workplace, so the option is offered but not selectable — it explains the
    // requirement rather than hiding the feature behind an empty picker.
    o.disabled = !ready;
    if (v.task?.kind === def.kind) o.selected = true;
    pick.append(o);
  }
  box.append(pick);

  const active = v.task ? orders.find((o) => o.def.kind === v.task!.kind) : undefined;
  if (v.task) {
    box.append(el('div', 'flex items-center justify-between gap-2 mt-1.5', {},
      el('div', 'text-[11px] text-text-dim min-w-0', {},
        el('div', 'truncate', {}, active?.def.blurb ?? v.task.name),
        el('div', 'font-mono text-gold', {}, chargeLabel(v.task, active?.def))),
      el('button',
        `px-2.5 py-1 rounded border text-[11px] shrink-0 ${v.task.charges > 0 ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
        { onClick: v.task.charges > 0 ? () => void doRunTask(c, v.id) : undefined }, 'Send them out')));
  }
  return box;
}

function renderVillage(c: FarmCtx, st: VillageState) {
  const totalPending = st.villagers.reduce((n, v) => n + v.pending, 0);
  // Absent on a server that predates tasks — the whole strip then simply never
  // renders, rather than throwing on an undefined catalogue.
  const taskTypes = st.taskTypes ?? [];
  const readyCharges = st.villagers.reduce((n, v) => n + (v.task?.charges ?? 0), 0);

  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[460px] max-w-[92vw] max-h-[86vh] overflow-y-auto shadow-lift sheet-card', {},
    el('div', 'flex justify-between items-center mb-1', {},
      el('h3', 'font-heading text-gold', {}, 'Village'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')),
    el('div', 'text-[11px] text-text-dim mb-1', {}, st.cap === 0
      ? 'Build a House first — each House lets one villager move in. They work while you\'re away.'
      : `${st.count}/${st.cap} villagers · build more Houses (+1) or a Farmhouse (+2) to make room.`),
    // Settlement tier: population is what unlocks the next batch of buildings.
    el('div', 'flex items-center justify-between text-[11px] mb-3 px-2 py-1 rounded border border-border', {},
      el('span', 'text-gold', {}, `${st.tier.name} · pop ${st.count}`),
      el('span', 'text-text-dim', {}, st.nextTier
        ? `${st.nextTier.minPop - st.count} more to reach ${st.nextTier.name}`
        : 'largest settlement reached')));

  // ── Your villagers + collect ────────────────────────────────────────────────
  card.append(el('div', 'flex items-center justify-between text-[11px] uppercase tracking-wide text-text-dim mt-1 mb-1', {},
    el('span', '', {}, 'Your villagers'),
    el('span', 'inline-flex items-center gap-1 text-coin', {}, ...richText(`ready: ${totalPending}`))));

  if (!st.villagers.length) {
    card.append(el('div', 'text-[12px] text-text-dim mb-2', {}, 'No villagers yet — summon one below.'));
  }
  for (const v of st.villagers) {
    const opts = optionsFor(v, st);
    // Job picker: every workplace this villager could work, plus "Idle". Changing
    // it banks whatever it had produced so far, then restarts the clock.
    const select = el('select', 'bg-surface border border-border rounded text-[11px] px-1 py-0.5 max-w-[130px]', {
      onChange: (e: Event) => {
        const val = (e.target as HTMLSelectElement).value;
        const wp = opts.find((w) => `${w.tx},${w.ty}` === val) ?? null;
        void doAssign(c, v.id, wp);
      },
    }) as HTMLSelectElement;
    select.append(el('option', '', { value: '' }, 'Idle (at home)'));
    for (const w of opts) {
      const o = el('option', '', { value: `${w.tx},${w.ty}` }, `${w.name} ×${w.bonus}`) as HTMLOptionElement;
      if (v.job === w.kind && v.jobTx === w.tx && v.jobTy === w.ty) o.selected = true;
      select.append(o);
    }
    if (!opts.length) {
      select.disabled = true;
      select.className += ' opacity-50 cursor-not-allowed';
    }

    const vname = villagerName(v.id, v.name);
    const row = el('div', 'p-2 mb-1.5 rounded border border-border', {},
      el('div', 'flex items-center gap-3', {},
        el('img', 'w-8 h-8 object-contain', { src: KIND_SPRITE[v.kind] ?? '', style: 'image-rendering:pixelated' }),
        el('div', 'flex-1 min-w-0', {},
          el('div', 'text-sm', {}, vname),
          // A task hire produces no farm column, so quoting it "0/h" reads as a
          // broken producer. It is labour: say what it does instead.
          el('div', 'text-[11px] text-text-dim font-mono capitalize', {},
            v.produces ? `${v.kind} · ${v.ratePerHour}/h` : `${v.kind} · does chores`),
          select),
        v.produces
          ? el('div', 'text-[12px] font-mono inline-flex items-center gap-1', {}, gicon(PRODUCE_ICON[v.produces] ?? 'wood', 14), `+${v.pending}`)
          : el('span', '', {}, ''),
        // Dismiss: the only way to free a home, and demolishing an occupied House is
        // refused by the server — so this button has to exist for the player to ever
        // get a House back. Confirmed, since it is permanent and unrefunded.
        el('button', 'text-text-dim hover:text-danger text-sm px-1', {
          title: `Dismiss ${vname} (frees their home — permanent, no refund)`,
          // Painted card, not window.confirm(): a native dialog freezes the farm's
          // ticker behind it and lands as a system sheet on a phone (owner:
          // "we make ingame confirmation, not via chrome").
          onClick: () => openConfirm({
            title: `Send ${vname} away?`,
            body: 'They leave for good and their home is freed.',
            detail: 'Anything they produced is banked first. The coins you paid are not refunded.',
            confirmLabel: `Send ${vname} away`,
            cancelLabel: 'Keep them',
            onConfirm: () => void doDismiss(c, v.id),
          }),
        }, gicon('xmark', 11))));

    const orders = tasksFor(v, taskTypes);
    if (orders.length) row.append(taskBlock(c, v, orders));
    card.append(row);
  }
  // ── Workplaces, and improving them ─────────────────────────────────────────
  //
  // OWNER: "where is the upgrade logic of the building, this increases the speed
  // of generating the goods from npc". There was none, and there was nowhere to
  // put one either: workplaces only appeared inside the job dropdown. They get
  // their own list now, because an upgrade the player cannot find is an upgrade
  // that does not exist.
  if (st.workplaces.length) {
    card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-2 mb-1', {}, 'Workplaces'));
    for (const w of st.workplaces) {
      const level = w.level ?? 1;
      const maxLevel = w.maxLevel ?? level;
      const cost = w.upgradeCost ?? null;
      const worked = w.workerName ? `${w.workerName} works here` : 'nobody works here';
      const row = el('div', 'p-2 mb-1.5 rounded border border-border flex items-center gap-2', {},
        el('div', 'flex-1 min-w-0', {},
          el('div', 'text-[12px]', {}, `${w.name} · Lv ${level}${maxLevel > 1 ? `/${maxLevel}` : ''}`),
          // Says what the level BUYS, in the two numbers the player feels: how
          // fast it produces and how much it holds while he is away.
          el('div', 'text-[11px] text-text-dim truncate', {}, `×${w.bonus} output · ${worked}`)));
      if (cost) {
        const afford = st.resources.wood >= cost.wood && st.resources.stone >= cost.stone;
        row.append(el('button',
          `px-2.5 py-1 rounded border text-[11px] flex-none ${afford ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
          {
            title: `Upgrade to level ${level + 1} — ${cost.coins} coins, ${cost.wood} wood, ${cost.stone} stone`,
            onClick: afford ? () => void doUpgrade(c, w) : undefined,
          },
          `Upgrade · ${cost.wood}🪵 ${cost.stone}🪨`));
      } else {
        row.append(el('span', 'text-[11px] text-text-dim flex-none', {}, 'Max'));
      }
      card.append(row);
    }
  }
  if (st.villagers.length && !st.workplaces.length) {
    card.append(el('div', 'text-[11px] text-text-dim mb-2', {},
      st.count >= 3
        ? 'Build a Lumber Hut, Mine Shaft or Forager Hut — a villager working one produces more than twice as fast.'
        : `Reach Hamlet (3 villagers) to build workplaces — a worker there produces more than twice as fast.`));
  }
  if (st.villagers.length) {
    const collectBtn = el('button',
      `w-full mt-1 mb-3 px-3 py-2 rounded border text-sm ${totalPending > 0 ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
      { onClick: totalPending > 0 ? () => void doCollect(c) : undefined }, 'Collect production');
    card.append(collectBtn);
  }

  // One button for every standing order at once — with two or three hires, running
  // them one by one is the same three taps every single visit.
  if (st.villagers.some((v) => v.task)) {
    card.append(el('button',
      `w-full mb-3 px-3 py-2 rounded border text-sm ${readyCharges > 0 ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
      { onClick: readyCharges > 0 ? () => void doRunAll(c) : undefined },
      readyCharges > 0 ? `Send everyone out (${readyCharges} ready)` : 'No work banked yet'));
  }

  // ── Animals ─────────────────────────────────────────────────────────────────
  // Its own panel: pens, stock, feed and produce are a system in their own right,
  // and folding them into this card would make the village a scroll.
  card.append(el('button', 'w-full mb-3 px-3 py-2 rounded border border-border text-sm hover:border-gold hover:text-gold',
    { onClick: () => openHusbandry(c) }, 'Animals & pens'));

  // ── Summon catalog ──────────────────────────────────────────────────────────
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-2 mb-1', {}, 'Summon'));
  const full = st.count >= st.cap;
  for (const t of st.types) {
    const canAfford = st.resources.wood >= t.costWood && st.resources.stone >= t.costStone;
    const can = !full && canAfford;
    card.append(el('div', 'flex items-center gap-3 p-2 mb-1.5 rounded border border-border', {},
      el('img', 'w-8 h-8 object-contain', { src: KIND_SPRITE[t.kind] ?? '', style: 'image-rendering:pixelated' }),
      el('div', 'flex-1', {},
        el('div', 'text-sm', {}, t.name),
        el('div', 'text-[11px] text-text-dim', {}, t.blurb),
        // Producers quote a rate; a task hire has none (produces: null) and quotes
        // the order it can carry instead — otherwise the Farmhand advertises "+0/h"
        // and reads as the worst villager in the game.
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1 flex-wrap', {},
          ...richText(`🪙 ${t.costCoins}${t.costWood ? ` · 🪵 ${t.costWood}` : ''}${t.costStone ? ` · 🪨 ${t.costStone}` : ''}`
            + (t.produces ? ` · +${t.ratePerHour}/h` : ''))),
        ...(t.produces ? [] : taskTypes.filter((tt) => tt.villagerKind === t.kind).map((tt) =>
          el('div', 'text-[11px] text-shroom font-mono', {},
            `${tt.name} — ${tt.chargesPerHour}/h, up to ${tt.maxCharges} · works from the ${BUILDINGS[tt.requiresJob]?.name ?? tt.requiresJob}`)))),
      el('button',
        `px-2.5 py-1 rounded border text-sm ${can ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
        { onClick: can ? () => void doSummon(c, t.kind) : undefined }, st.cap === 0 ? 'Need House' : full ? 'Full' : 'Summon')));
  }

  closePlanter(c);
  // openOverlay (not a bare append): it attaches Escape, so every panel has a way
  // out even when the page chrome its close button sits under is hidden.
  const wrap = modalWrap(() => closePlanter(c), card);
  openOverlay(c, wrap);
}

async function doSummon(c: FarmCtx, kind: string) {
  try {
    const { tx, ty } = homeTileNearPlayer(c);
    await dispatchAction('village.summon', async (actionId) => {
      const st = await villageApi.summon(kind, tx, ty, actionId);
      toast('Villager summoned!');
      renderVillage(c, st);
      void refreshMapVillagers(c, st);
      await c.game.refreshHud();
      refreshBag(c);
    }, { key: kind });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Summon failed', 'error');
  }
}

/**
 * Improve a workplace.
 *
 * The banked figure is reported because the upgrade BANKS the worker's pending
 * produce at the old rate before the new one applies (village.service). Without
 * saying so, a player who upgrades a full hut sees his pending counter reset to
 * zero and reasonably concludes the upgrade ate his wood.
 */
async function doUpgrade(c: FarmCtx, w: VillageWorkplace) {
  try {
    await dispatchAction('village.upgrade', async (actionId) => {
      const r = await villageApi.upgrade(w.tx, w.ty, actionId);
      // The banked figure is reported because the upgrade BANKS the worker's
      // pending produce at the OLD rate before the new one applies (see
      // village.service). Say so, or a player who upgrades a full hut watches his
      // pending counter reset to zero and concludes the upgrade ate his wood.
      toast(r.banked > 0
        ? `${w.name} is now level ${r.level} — banked +${r.banked} ${r.resource ?? ''}`.trim()
        : `${w.name} is now level ${r.level}`);
      renderVillage(c, r.village);
      await c.game.refreshHud();
      refreshBag(c);
    }, { key: `${w.tx},${w.ty}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not upgrade that workplace', 'error');
  }
}

async function doAssign(c: FarmCtx, villagerId: string, wp: VillageWorkplace | null) {
  try {
    await dispatchAction('village.assign', async (actionId) => {
      const r = await villageApi.assign(villagerId, wp?.tx ?? null, wp?.ty ?? null, actionId);
      toast(r.banked > 0
        ? `${wp ? `Off to the ${wp.name}` : 'Sent home'} — banked +${r.banked} ${r.resource}`
        : (wp ? `Off to the ${wp.name}` : 'Sent home'));
      renderVillage(c, r.village);
      void refreshMapVillagers(c, r.village);
      await c.game.refreshHud();
      refreshBag(c);
    }, { key: villagerId });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not change job', 'error');
  }
}

async function doDismiss(c: FarmCtx, villagerId: string) {
  try {
    await dispatchAction('village.dismiss', async (actionId) => {
      const r = await villageApi.dismiss(villagerId, actionId);
      toast(r.banked > 0 ? `${r.name} left — banked +${r.banked} ${r.resource}` : `${r.name} left the village`);
      renderVillage(c, r.village);
      void refreshMapVillagers(c, r.village);
      await c.game.refreshHud();
      refreshBag(c);
    }, { key: villagerId });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not dismiss', 'error');
  }
}

/**
 * Give or clear a standing order. The response carries only the villager's new
 * task, so the panel is re-read from the server rather than patched locally —
 * charges are derived on read and a hand-patched copy would immediately drift.
 */
async function doSetTask(c: FarmCtx, villagerId: string, kind: string | null) {
  try {
    await dispatchAction('village.task', async (actionId) => {
      const r = await villageApi.setTask(villagerId, kind, actionId);
      toast(r.task ? `Standing order: ${r.task.name}` : 'Order cancelled');
      renderVillage(c, await villageApi.get());
    }, { key: `${villagerId}:${kind ?? 'none'}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not set the order', 'error');
  }
}

/**
 * Carry out one villager's order. A run waters beds and/or moves feed and produce
 * through the bag, so the farm view, the bag and the HUD all have to be re-read —
 * the client never computes what the run produced.
 */
async function doRunTask(c: FarmCtx, villagerId: string) {
  try {
    await dispatchAction('village.task.run', async (actionId) => {
      const r = await villageApi.runTask(villagerId, actionId);
      toast(describeRun(r));
      await afterRuns(c, [r]);
    }, { key: villagerId });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not run the order', 'error');
  }
}

async function doRunAll(c: FarmCtx) {
  try {
    await dispatchAction('village.task.runAll', async (actionId) => {
      const { runs } = await villageApi.runAllTasks(actionId);
      const used = runs.reduce((n, r) => n + r.used, 0);
      toast(used > 0 ? runs.map(describeRun).join(' · ') : 'Nothing to do just now');
      await afterRuns(c, runs);
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not run the orders', 'error');
  }
}

/** Re-sync everything a run can have touched, then repaint the village panel. */
async function afterRuns(c: FarmCtx, runs: TaskRun[]) {
  // Only reload the farm when beds actually changed: `load()` rebuilds the plot
  // layer, which is far too heavy to run for a herder that only touched the bag.
  if (runs.some((r) => r.kind === 'tend_plots' && r.used > 0)) await c.load();
  await c.game.refreshHud();
  refreshBag(c);
  renderVillage(c, await villageApi.get());
}

async function doCollect(c: FarmCtx) {
  try {
    await dispatchAction('village.collect', async (actionId) => {
      const r = await villageApi.collect(actionId);
      const g = r.granted;
      const parts = [g.wood && `+${g.wood} wood`, g.stone && `+${g.stone} stone`, g.mushroom && `+${g.mushroom} mushroom`].filter(Boolean);
      toast(parts.length ? `Collected ${parts.join(', ')}` : 'Nothing ready yet');
      renderVillage(c, r.village);
      await c.game.refreshHud();
      refreshBag(c);
    });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Collect failed', 'error');
  }
}
